/**
 * ÉÉN bron voor de eigen bedrijfsgegevens van Glastotaal. Staat in `packages/core` zodat zowel de
 * client (contactpagina, factuur, juridische pagina's) als de Cloud Functions (bevestigingsmail)
 * exact dezelfde gegevens gebruiken. Dit zijn de gegevens van Glastotaal zélf, NIET die van Timmers.
 *
 * Ingevuld op 29-07-2026 met de echte gegevens (KvK-nummer, btw-nummer, vestigingsadres en
 * contactgegevens zijn in NL wettelijk verplicht voor een webshop). De [placeholder]-afhandeling
 * blijft staan: een veld dat ooit leeg raakt, verdwijnt netjes van de factuur in plaats van als
 * "[Telefoonnummer]" op papier te belanden.
 */
export const COMPANY = {
  /** Handelsnaam zoals getoond aan klanten. */
  name: "Glastotaal",
  /** Statutaire/handelsnaam op de factuur (mag gelijk zijn aan name). */
  legalName: "Glastotaal",

  /** Vestigings-/bezoekadres. */
  street: "Basicweg 17",
  postalCode: "3821 BR",
  city: "Amersfoort",
  country: "Nederland",

  email: "info@glastotaal.nl",
  phone: "+31 33 456 2302",
  /** Openingstijden voor de klantenservice. */
  hours: "ma–vr 09:00–17:00",

  kvk: "42122273",
  vat: "NL869828319B01",
  iban: "NL31 RABO 0176 7954 80",
  /** Tenaamstelling van die rekening — moet exact matchen met wat de bank kent (SEPA-bestand). */
  ibanName: "Glastotaal",
} as const;

/**
 * Waar Glastotaal nog meer als bedrijf te vinden is. Deze lijst gaat als `sameAs` mee in de
 * Store-structuurdata (SiteLayout): daarmee vertel je Google dat glastotaal.nl, het
 * Google-bedrijfsprofiel en het Kiyoh-profiel één en hetzelfde bedrijf zijn. Dat voedt het
 * kennispaneel en de lokale resultaten — het is géén manier om reviewsterren te krijgen.
 *
 * Alleen profielen die echt bestaan en van ons zijn. Een lege of gegokte URL is schadelijker dan
 * geen URL: Google koppelt dan de verkeerde entiteit aan je site.
 */
export const COMPANY_PROFILES: readonly string[] = [
  "https://www.linkedin.com/company/glastotaal-linkedin",
  "https://www.kiyoh.com/reviews/1081255/Glastotaal",
  // Google-bedrijfsprofiel ("Glastotaal.nl", Basicweg 17). Dit is de CID-vorm en niet de lange
  // /maps/place/…-URL uit de adresbalk: die bevat coördinaten en een `g_ep`-parameter die Google
  // periodiek vernieuwt, dus die verloopt. De CID hoort bij de vermelding zelf en blijft staan.
  "https://maps.google.com/?cid=15901333260887508375",
];

/** Een placeholder-waarde ("[…]" of leeg) telt als "nog niet ingevuld" → weglaten op de factuur. */
export function isPlaceholder(v: string | undefined): boolean {
  return !v || v.trim() === "" || v.trim().startsWith("[");
}

/** Adres als losse regels (placeholders eruit). */
export function companyAddressLines(): string[] {
  return [COMPANY.street, `${COMPANY.postalCode} ${COMPANY.city}`.trim(), COMPANY.country].filter(
    (l) => !isPlaceholder(l),
  );
}

/** Adres als één regel. */
export function companyAddressOneLine(): string {
  return `${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}`;
}

/**
 * Onze leverancier Timmers, en ons klantnummer bij hen.
 *
 * Eén bron, want dit stond op vijf plekken los ingetypt: de bestelbon, twee retourformulieren,
 * de retour-afhandeling en de retour-mail. Toen het klantnummer wijzigde (509157 → 509424,
 * 07-08-2026) liep er één mee en bleven de andere vier op het oude nummer staan — documenten
 * die naar Timmers gaan met een nummer waarop zij ons niet kunnen thuisbrengen.
 */
export const SUPPLIER_TIMMERS = {
  name: "Timmers B.V.",
  /** Ons dealerklantnummer bij Timmers. Staat op elke bon en elk retourformulier. */
  customerNumber: "509424",
  street: "Krombraak 8",
  postalCode: "4906 CR",
  city: "Oosterhout",
  phone: "0162-461175",
  /** Algemeen adres (facturen, vragen). Bestellingen gaan naar orderEmail. */
  email: "info@timmers-bv.nl",
  /** Waar bestellingen heen moeten (doorgegeven 07-08-2026). */
  orderEmail: "bestelling@timmers-bv.nl",
  web: "www.timmers-bv.nl",
  kvk: "20122320",
  vat: "NL8152.47.199B01",
  iban: "NL87RABO0182469069",
  /** Retouren gaan naar een eigen afdeling — zo staat het op hun retourformulier. */
  returnAddress: ["Timmers B.V.", "t.a.v. afdeling Retouren", "Krombraak 8", "4906 CR Oosterhout"],
} as const;
